import { CSSResult, LitElement, PropertyValueMap, TemplateResult, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import css from "./maz-plushie-gallery.scss";
import { plushieData } from "../../data/plushies";

 type Plushie = {
  name: string;
  path: string;
 }

@customElement("maz-plushie-gallery")
export class MaziCraftPlushieGallery extends LitElement {
  static get styles(): CSSResult { return css; }

  /**
   * The name
   */
  @property({type: String, reflect: true}) theme: string = "";
  
  @query("#theme") themeElement: any;
  @query("#filter") filterElement: any;
  
  @state() selectedTheme: string = "";
  @state() selectedPlushie: any;
  @state() filterText: string = "";
  
  private data: any = {};
  private hook: any = null;

  /**
   * Called when the application component is first attached to the DOM
   * @param changedProperties	Map of changed properties
   */
  firstUpdated(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>) {
    super.firstUpdated(changedProperties);
    if(this.theme != "") {
      this.themeElement.value = this.theme;
    } else {
      this.themeElement.value = "scarworld-s6";
    }
    this.selectTheme();
  }

  /**
   * Method to handle a search key up event
   */
  private updateFilter() {
    this.filterText = this.filterElement.value;
  }

  /**
   * Method to select a theme
   */
  private selectTheme() {
    this.stopRandomSelection();
    this.selectedTheme = this.themeElement.value;
    if(this.selectedTheme == "all") {
      this.data = { name: "all", plushies: plushieData.flatMap(parent => parent.plushies) };
      console.log(this.data);
    } else {
      this.data = plushieData.find(theme => theme.code == this.selectedTheme);
    }
    this.selectedPlushie = this.data.plushies[0];
    this.filterText = this.filterElement.value = "";
    this.startRandomSelection();
  }

  /**
   * Method to start random selection
   */
  private startRandomSelection() {
    this.hook = setTimeout(() => { this.randomSelect() }, 10500);
  }

  /**
   * Method to stop random selection
   */
  private stopRandomSelection() {
    clearTimeout(this.hook);
  }

  /**
   * Method to select a random plushie
   */
  private randomSelect() {
    this.selectedPlushie = this.data.plushies[Math.floor(Math.random()*this.data.plushies.length)];
    this.startRandomSelection();
  }

  /**
   * Method to select a plushie from the gallery
   * @param plushie 
   */
  private selectPlushie(plushie: any) {
    this.stopRandomSelection();
    this.selectedPlushie = plushie;
  }

  /**
   * Method to render the theme options
   * @returns HTML Template
   */
  private renderThemeOptions(): TemplateResult[] {
    const themes = plushieData.map(theme => { return { name: theme.name, code: theme.code }; });
    return themes
      .sort((a, b) => a.name.localeCompare(b.name, "en", { numeric: true, sensitivity: "base" }))
      .map(theme => { 
        return html`<option value="${theme.code}">${theme.name}</option>`; 
      });
  }

  /**
   * Method to render the controls
   * @returns HTML Template
   */
  private renderControls(): TemplateResult {
    return html`
      <div class="controls">
        <div class="form-control">
          <label for="theme">Theme</label>
          <select
            id="theme"
            name="theme"
            @change=${() => this.selectTheme()}
          >
            <option value="all">All</option>
            ${this.renderThemeOptions()}
          </select>
        </div>
        <div class="form-control">
          <label for="filter">Filter</label>
          <input
            id="filter"
            name="filter"
            type="text"
            @keyup=${() => this.updateFilter()}
          />
        </div>
      </div>
    `;
  }

  /**
   * Render the preview, details and download button
   * @returns HTML Template
   */
  private renderPreview(): TemplateResult {
    if(!this.selectedPlushie) {
      return html``;
    } else {
      return html`
        <div class="preview">
          <div class="plushie">
            <maz-plushie 
              mode="3D"
              skinUrl="/assets/plushies/${this.selectedPlushie.path}.png" 
              name="${this.selectedPlushie.name}"
            ></maz-plushie>
          </div>
          <div class="skin">
            <maz-plushie-skin 
              skinUrl="/assets/plushies/${this.selectedPlushie.path}.png" 
              name="${this.selectedPlushie.name}"
            ></maz-pliushie-skin>
          </div>
          <div class="details">
            <h1>${this.selectedPlushie.name}</h1>
            <a class="link-button" href="/assets/plushies/${this.selectedPlushie.path}.png" title="Click to download this plushie skin" download>Download</a>
          </div>
        </div>
      `;
    }
  }

  /**
   * Render the thumbnail panel
   * @returns HTML Template
   */
  private renderThumbnails(): TemplateResult {
    // Render nothing if no theme is selected or the selected theme is not valid
    if(!this.selectedTheme || this.selectedTheme == "") {
      return html``;
    }

    // Filter data if applicable
    let filtered = {...this.data};
    filtered.plushies = filtered.plushies.filter((plushie: { name: string; }) => { return plushie.name.toLowerCase().includes(this.filterText.toLowerCase())} );

    // Render thumbnail gallery
    return html`
      <div class="gallery">
        <div class="header">
          <h2>Showing ${filtered.plushies.length} plushies:</h2>
        </div>        
        <div class="thumbnails">
          ${filtered.plushies
            .sort((a: Plushie, b: Plushie) => a.name.localeCompare(b.name, "en", { numeric: true, sensitivity: "base" }))
            .map((plushie: Plushie) => {
              return html`
                <maz-plushie-thumbnail 
                  skinUrl="/assets/plushies/${plushie.path}.png" 
                  name="${plushie.name}"
                  @click=${() => this.selectPlushie(plushie)}
                ></maz-plushie-thumbnail>
              `;
            })}
        </div>
      </div>
    `;
  }

  /**
   * Render the plushie gallery
   * @returns HTML Template
   */
  render(): TemplateResult {
    return html`<div class="container">
      <div class="header">
        <div><h1>Plushie Gallery</h1></div>
        ${this.renderControls()}
      </div>
      ${this.renderPreview()}
      ${this.renderThumbnails()}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "maz-plushie-gallery": MaziCraftPlushieGallery;
  }
}
